import { useEffect, useState } from 'react'
import { get, remove, pull, isEqual, pick } from 'lodash-es'
import { createContainer } from 'unstated-next'
import { useImmerReducer } from 'use-immer'
import { useQuery } from '@tanstack/react-query'

import {
  IPlugProperty,
  ITag,
  IRawDataProperty,
  IWebscriptPlugProperties,
} from '~/lib/types'
import clientContainer from '~/lib/client'
import {
  PlugType,
  PlugV2Structure,
  PluginStatusOwnership,
} from '~/components/PluginsAndWebscripts/Common/Types'
import { CustomFunctionContainer } from '~/components/PluginsAndWebscripts/Common/useCustomFunctions'
import { IPlug } from '~/components/Templates/util'
import { useLogin } from '~/components/App/LoginContext'
import { PLUG_VERSIONS_KEY } from '~/lib/QueryKeys'

interface IChanges {
  payload: boolean
  metadata: boolean
  interface: boolean
  manifest: boolean
}

enum EditorStateType {
  Empty,
  SetStateProperty,
  ToggleEditProperty,
  ToggleEditRawData,
  ToggleEditDependency,
  ToggleEditStates,
  AddState,
  RemoveState,
  AddProperty,
  EditProperty,
  RemoveProperty,
  AddRawData,
  RemoveRawData,
  AddDependency,
  RemoveDependency,
  Replace,
  AddTag,
  RemoveTag,
  SetDocumentation,
  SetDescription,
  SetTestValue,
  UpdateDeployStatus,
}

export enum EConfigurationType {
  string = 'string',
  integer = 'integer',
  long = 'long',
  double = 'double',
  float = 'float',
  boolean = 'boolean',
  object = 'object',
}

export enum EConfigurationFormatType {
  enum = 'enum',
  resource = 'resource',
  vault = 'vault',
  duration = 'duration',
  url = 'url',
  date = 'date',
}

interface IAction {
  type: EditorStateType
  payload?: any
}

interface IPropFormat {
  type: string
  values?: any[]
  strict: boolean
}

const PAYLOAD_STATE_PROPS = ['script', 'dependencies']
const METADATA_STATE_PROPS = ['author', 'description', 'iconURL']
const DOCUMENTATION_STATE_PROPS = ['documentation']
const INTERFACE_STATE_PROPS = [
  'states',
  'properties',
  'rawData',
  'runtimeName',
  'runtimeVersion',
]
// TODO: add webscript manifest properties once we add patch manifest support for webscripts
// Properties: webscriptStatus (private), allowHmac
const MANIFEST_STATE_PROPS = [
  'states',
  'properties',
  'rawData',
  'runtimeName',
  'runtimeVersion',
]

export interface IPluginEditorContextState {
  // keep the initial fetched state from backend in memory for later difference checking
  initialState: Omit<IPluginEditorContextState, 'initialState'>
  runtimeName: string
  runtimeVersion: string
  // keeps track of what entity is being edited currently
  type: PlugType
  pluginOwnershipStatus: PluginStatusOwnership
  // plug related properties
  isDeprecated: boolean
  isPlugProtected: boolean
  reloadVersions?: any
  name: string
  author: string
  draftDetails: any
  script: string
  version: string
  versions: string[]
  iconURL: string
  states: string[]
  properties: IPlugProperty[]
  rawData: IRawDataProperty[]
  dependencies: object
  deployStatus: string
  description: string
  tags: string[]
  plug: any
  documentation?: {
    states: Array<{
      name: string
      description?: string
    }>
    input: Array<{ name: string; description?: string }>
    output: Array<{ name: string; description?: string }>
    examples?: Array<{
      description?: string
      state?: string
      input?: Record<string, any>
      output?: Record<string, any>
    }>
  }

  // editor configuration
  enabledPropertiesEditor: boolean
  enabledDependenciesEditor: boolean
  enabledStatesEditor: boolean
  enabledRawDataEditor: boolean

  // callable functions
  currentState: any
  addDependency: (name: string, version?: string, isPython?: boolean) => void
  removeDependency: (dependency: string) => void
  addTag: (tag: ITag) => void
  removeTag: (name: string) => void
  addProperty: (
    name: string,
    type: string,
    required: boolean,
    format?: IPropFormat,
  ) => void
  editProperty: (
    oldName: string,
    newName: string,
    type: string,
    required: boolean,
    format?: IPropFormat,
  ) => void
  removeProperty: (name: string) => void
  addRawData: (name: string, type: string) => void
  removeRawData: (name: string) => void
  addState: (name: string) => void
  removeState: (name: string) => void
  toggleEditStates: () => void
  toggleEditProperty: () => void
  toggleEditRawData: () => void
  toggleEditDependency: () => void
  toggleWebscriptStatus: () => void
  setWebscriptStatus: (value: boolean) => void
  setStateProperty: (name: keyof IPluginEditorContextState, value: any) => void
  setDocumentation: (name: string, value: string) => void
  setDescription: (value: string) => void
  setTestValue: (propertyName: string, value: any) => void
  updateDeployStatus: (status: string) => void
}

const emptyState: Partial<IPluginEditorContextState> = {
  type: PlugType.Sensor,
  states: [],
  properties: [],
  rawData: [],
  versions: [],
  dependencies: {},
  enabledPropertiesEditor: false,
  enabledDependenciesEditor: false,
  enabledStatesEditor: false,
  enabledRawDataEditor: false,
}

function reducer(draft: any, action: IAction) {
  const { type, payload } = action

  if (!draft.documentation) {
    draft.documentation = {
      input: [],
      output: [],
      states: [],
      examples: [],
    }
  }

  switch (type) {
    case EditorStateType.SetStateProperty:
      draft[payload.property] = payload.value
      break
    case EditorStateType.ToggleEditProperty:
      draft.enabledPropertiesEditor = !draft.enabledPropertiesEditor
      break
    case EditorStateType.ToggleEditRawData:
      draft.enabledRawDataEditor = !draft.enabledRawDataEditor
      break
    case EditorStateType.ToggleEditDependency:
      draft.enabledDependenciesEditor = !draft.enabledDependenciesEditor
      break
    case EditorStateType.ToggleEditStates:
      draft.enabledStatesEditor = !draft.enabledStatesEditor
      break
    case EditorStateType.AddState:
      draft.documentation.states.push({ name: payload.name, description: '' })
      draft.states.push(payload.name)
      break
    case EditorStateType.RemoveState:
      remove(draft.documentation.states, {
        name: payload.name,
      })
      pull(draft.states, payload.name)
      break
    case EditorStateType.AddProperty:
      draft.documentation.input.push({ name: payload.name, description: '' })
      draft.properties.push(payload)
      break
    case EditorStateType.EditProperty:
      remove(draft.documentation.input, {
        name: payload.oldName,
      })

      draft.documentation.input.push({
        name: payload.newName,
        description: '',
      })

      remove(draft.properties, { name: payload.oldName })
      draft.properties.push({
        name: payload.newName,
        dataType: payload.dataType,
        mandatory: payload.mandatory,
        format: payload.format,
      })
      break
    case EditorStateType.RemoveProperty:
      remove(draft.documentation.input, { name: payload.name })
      remove(draft.properties, { name: payload.name })
      break
    case EditorStateType.AddRawData:
      draft.documentation.output.push({
        name: payload.name,
        description: '',
      })
      draft.rawData.push(payload)
      break
    case EditorStateType.RemoveRawData:
      remove(draft.documentation.output, {
        name: payload.name,
      })
      remove(draft.rawData, { name: payload.name })
      break
    case EditorStateType.AddDependency:
      if (!payload.isPython) {
        draft.dependencies[payload.name] = payload.version
      } else {
        draft.dependencies.push(payload.name)
      }

      break
    case EditorStateType.RemoveDependency:
      if (!payload.isPython) {
        delete draft.dependencies[payload.name]
      } else {
        const dependenciesIndex = draft.dependencies.indexOf(payload.name)
        if (dependenciesIndex > -1) {
          // only splice array when item is found
          draft.dependencies.splice(dependenciesIndex, 1) // 2nd parameter means remove one item only
        }
      }
      break

    case EditorStateType.Replace:
      Object.keys(draft).forEach(key => {
        delete draft[key]
      })
      Object.assign(draft, { ...emptyState, ...payload })
      break
    case EditorStateType.AddTag:
      if (!draft.tags) {
        draft.tags = []
      }

      draft.tags.push(payload)
      break
    case EditorStateType.RemoveTag:
      draft.tags.splice(
        draft.tags.findIndex(tag => tag.name === payload),
        1,
      )
      break
    case EditorStateType.SetDocumentation:
      if (payload.documentationType === 'examples') {
        draft.documentation.examples = payload.value
        break
      }

      if (!draft.documentation[payload.documentationType]) {
        draft.documentation[payload.documentationType] = []
      }

      // eslint-disable-next-line no-case-declarations
      const index = draft.documentation[payload.documentationType].findIndex(
        ({ name, parameter }) =>
          name === payload.name || parameter === payload.name,
      )

      if (index !== -1) {
        draft.documentation[payload.documentationType][index].description =
          payload.value
      }
      // WORKAROUND - name property does not appear in the documentation object from BE, if we don't find the property we will add it.
      if (index === -1) {
        draft.documentation[payload.documentationType].push({
          name: payload.name,
          description: payload.value,
        })
      }
      break
    case EditorStateType.SetDescription:
      draft.metadata.description = payload.value
      break
    case EditorStateType.SetTestValue: {
      if (!draft.documentation.examples) {
        draft.documentation.examples = []
      }

      let latestExampleIndex = draft.documentation.examples.length - 1
      if (latestExampleIndex === -1) {
        const newExample: any = {
          description: 'Test example',
          input: {},
          output: {},
        }
        draft.documentation.examples.push(newExample)
        latestExampleIndex = 0
      }

      // Update the input with the new test value in the latest example
      const latestExample = draft.documentation.examples[latestExampleIndex]
      if (
        latestExample?.state &&
        Array.isArray(draft.states) &&
        !draft.states.includes(latestExample.state)
      ) {
        delete latestExample.state
      }
      if (!latestExample.input) {
        latestExample.input = {}
      }
      latestExample.input[payload.propertyName] = payload.value
      break
    }
    case EditorStateType.UpdateDeployStatus:
      draft.deployStatus = action.payload
      break
    default:
      break
  }
}

const fetchPlugVersions = async name => {
  if (name) {
    const versions = await clientContainer.client.registry.plugs.listVersions(
      name,
      {
        status: 'any',
        includeDraft: true,
      },
    )
    return versions
  }
  return []
}

const usePluginEditor = (data: PlugV2Structure): IPluginEditorContextState => {
  const [state, dispatch] = useImmerReducer(reducer, emptyState)
  const { isSaveEventPending } = CustomFunctionContainer.useContainer()
  const [initialState, setInitialState] =
    useState<Partial<IPlug & IWebscriptPlugProperties>>()
  const [changes, setChanges] = useState<IChanges>({
    payload: false,
    metadata: false,
    interface: false,
    manifest: false,
  })

  useEffect(() => {
    const payloadState = pick(state, PAYLOAD_STATE_PROPS)
    const payloadInitialState = pick(initialState, PAYLOAD_STATE_PROPS)

    const metadataState = pick(state, METADATA_STATE_PROPS)
    const metadataInitialState = pick(initialState, METADATA_STATE_PROPS)

    const documentationState = pick(state, DOCUMENTATION_STATE_PROPS)
    const documentationInitialState = pick(
      initialState,
      DOCUMENTATION_STATE_PROPS,
    )

    const interfaceState = pick(state, INTERFACE_STATE_PROPS)
    const interfaceInitialState = pick(initialState, INTERFACE_STATE_PROPS)

    const manifestState = pick(state, MANIFEST_STATE_PROPS)
    const manifestInitialState = pick(initialState, MANIFEST_STATE_PROPS)

    setChanges({
      payload: !isEqual(payloadState, payloadInitialState),
      metadata: !isEqual(metadataState, metadataInitialState),
      interface:
        !isEqual(interfaceState, interfaceInitialState) ||
        !isEqual(documentationState, documentationInitialState),
      manifest: !isEqual(manifestState, manifestInitialState),
    })
  }, [initialState, state])

  const { sub } = useLogin()

  const versions = useQuery({
    queryKey: [
      PLUG_VERSIONS_KEY,
      data.details.plug.name,
      data.details.plug.version,
      data.details.draft,
    ],
    queryFn: async () => await fetchPlugVersions(data.details.plug.name),
    refetchOnWindowFocus: false,
    gcTime: 0,
  })

  const reloadVersions = () => {
    versions.refetch()
  }

  useEffect(() => {
    if (!data || isSaveEventPending) return

    const pluginOwnershipStatus =
      get(data, 'details.draft', '') === false
        ? PluginStatusOwnership.PUBLISHED
        : get(data, 'details.createdBy', '') === sub
          ? PluginStatusOwnership.DRAFT_OWNED
          : PluginStatusOwnership.DRAFT_NOT_OWNER

    const name = get(data, 'details.plug.name', '')
    const author = get(data, 'details.plug.metadata.author', '')

    const iconURL = get(data, 'details.plug.metadata.iconURL', '')
    const description = get(data, 'details.plug.metadata.description', '')
    const version = get(data, 'details.plug.version', '1.0.0')
    const script = get(data, 'script', '')
    const deployStatus = get(data, 'details.status')
    const tags = get(data, 'details.plug.tags', [])
    const isDeprecated = get(data, 'details.deprecated', false)
    const isPlugProtected = get(data, 'details.plug.protected', false)

    const type = get(data, 'details.plug.type')
    const properties = get(data, 'details.plug.interface.input', [])
    const rawData = get(data, 'details.plug.interface.output', [])
    const states = get(data, 'details.plug.interface.states', [])
    const runtime = get(data, 'runtime', {})
    const runtimeName = get(
      data,
      'details.runtime.name',
      get(data, 'details.plug.runtime', ''),
    )
    const runtimeVersion = get(
      data,
      'details.runtime.version',
      get(data, 'details.plug.runtimeVersion', ''),
    )

    const manifest = get(data, 'manifest')

    const dependenciesInitial = runtimeName.includes('python') ? [] : {}

    let dependencies = get(data, 'dependencies', '')
    if (dependencies === '') {
      dependencies = dependenciesInitial
    }
    const plugVersions = get(versions, 'data.entities', [])

    const draft = plugVersions.find(version => version.draft)

    const draftDetails = {
      hasDraft: !!draft,
      name: draft ? draft.plug.name : '',
      version: draft ? draft.plug.version : '',
    }

    const docsInput = get(data, 'details.plug.metadata.documentation.input', [])
    const docsOutput = get(
      data,
      'details.plug.metadata.documentation.output',
      [],
    )
    const docsStates = get(
      data,
      'details.plug.metadata.documentation.states',
      [],
    )
    const docsExamples = get(
      data,
      'details.plug.metadata.documentation.examples',
      [],
    )
    const documentation = {
      input: docsInput,
      output: docsOutput,
      states: docsStates,
      examples: docsExamples,
    }

    const payload = {
      pluginOwnershipStatus,
      name,
      version,
      description,
      author,
      iconURL,
      dependencies,
      script,
      deployStatus,
      type,
      isDeprecated,
      isPlugProtected,
      tags,
      states,
      documentation,
      properties,
      rawData,
      runtimeName,
      runtimeVersion,
      runtime,
      manifest,
      versions: plugVersions,
      draftDetails,
      reloadVersions,
    }

    // @ts-expect-error incompatible types
    setInitialState(payload)

    dispatch({
      type: EditorStateType.Replace,
      payload,
    })
  }, [JSON.stringify(data), versions?.data, versions.isLoading])

  const setStateProperty = (property: string, value: any) =>
    dispatch({
      type: EditorStateType.SetStateProperty,
      payload: { property, value },
    })

  const addProperty = (
    name: string,
    type: string,
    required: boolean,
    format?: IPropFormat,
  ) =>
    dispatch({
      type: EditorStateType.AddProperty,
      payload: {
        name,
        dataType: type,
        mandatory: required,
        format,
      },
    })

  const editProperty = (
    oldName: string,
    newName: string,
    type: string,
    required: boolean,
    format?: IPropFormat,
  ) =>
    dispatch({
      type: EditorStateType.EditProperty,
      payload: {
        oldName,
        newName,
        dataType: type,
        mandatory: required,
        format,
      },
    })

  const removeProperty = (name: string) =>
    dispatch({
      type: EditorStateType.RemoveProperty,
      payload: { name },
    })

  const addRawData = (name: string, type: string) =>
    dispatch({
      type: EditorStateType.AddRawData,
      payload: { name, dataType: type },
    })

  const removeRawData = (name: string) =>
    dispatch({
      type: EditorStateType.RemoveRawData,
      payload: { name },
    })

  const addState = (name: string) =>
    dispatch({
      type: EditorStateType.AddState,
      payload: { name },
    })

  const removeState = (name: string) =>
    dispatch({
      type: EditorStateType.RemoveState,
      payload: { name },
    })

  const addDependency = (name: string, version: string, isPython?: boolean) =>
    dispatch({
      type: EditorStateType.AddDependency,
      payload: { name, version, isPython },
    })

  const removeDependency = (name: string, isPython?: boolean) =>
    dispatch({
      type: EditorStateType.RemoveDependency,
      payload: { name, isPython },
    })

  const addTag = (tag: ITag) =>
    dispatch({
      type: EditorStateType.AddTag,
      payload: tag,
    })

  const removeTag = (tagName: string) =>
    dispatch({
      type: EditorStateType.RemoveTag,
      payload: tagName,
    })

  const setDocumentation = (
    documentationType: string,
    name: string,
    value: string,
  ) => {
    dispatch({
      type: EditorStateType.SetDocumentation,
      payload: {
        documentationType,
        name,
        value,
      },
    })
  }

  const setDescription = (value: string) => {
    dispatch({
      type: EditorStateType.SetDescription,
      payload: {
        value,
      },
    })
  }

  const setTestValue = (propertyName: string, value: any) => {
    dispatch({
      type: EditorStateType.SetTestValue,
      payload: { propertyName, value },
    })
  }

  const save = async () => {
    if (
      !changes?.metadata &&
      !changes?.interface &&
      !changes?.manifest &&
      !changes?.payload
    ) {
      return { success: false, message: 'Nothing to save' }
    }

    try {
      // Save metadata changes (including documentation with examples)
      if (changes?.metadata) {
        const metadataToUpdate: any = {}
        const currentState = state

        // Always include all metadata fields for complete update
        metadataToUpdate.author = currentState.author
        metadataToUpdate.description = currentState.description
        metadataToUpdate.iconURL = currentState.iconURL
        metadataToUpdate.tags = currentState.tags

        // Include documentation (which contains examples)
        metadataToUpdate.documentation = currentState.documentation

        await clientContainer.client.registry.plugs.patchMetadata(
          currentState.name,
          currentState.version,
          metadataToUpdate,
        )
      }

      if (changes?.interface) {
        const currentState = state
        await clientContainer.client.registry.plugs.patchInterface(
          currentState.name,
          currentState.version,
          {
            states: currentState.states,
            input: currentState.properties,
            output: currentState.rawData,
          },
        )
      }

      setInitialState(state)

      return { success: true, message: 'Successfully saved' }
    } catch (error) {
      const message =
        get(error, 'response.data.message') ??
        get(error, 'response.data.error') ??
        'Unknown error'
      return { success: false, message: `Failed to save: ${message}` }
    }
  }

  const updateDeployStatus = (status: string) =>
    dispatch({
      type: EditorStateType.UpdateDeployStatus,
      payload: status,
    })

  const toggleEditDependency = () =>
    dispatch({ type: EditorStateType.ToggleEditDependency })
  const toggleEditProperty = () =>
    dispatch({ type: EditorStateType.ToggleEditProperty })
  const toggleEditRawData = () =>
    dispatch({ type: EditorStateType.ToggleEditRawData })
  const toggleEditStates = () =>
    dispatch({ type: EditorStateType.ToggleEditStates })

  return {
    ...state,
    initialState,
    currentState: state,
    setInitialState,
    addProperty,
    editProperty,
    removeProperty,

    addRawData,
    removeRawData,

    addState,
    removeState,

    setDocumentation,
    setDescription,
    setTestValue,

    addDependency,
    removeDependency,

    addTag,
    removeTag,

    toggleEditStates,
    toggleEditProperty,
    toggleEditRawData,
    toggleEditDependency,

    setStateProperty,
    plug: data.details,
    updateDeployStatus,
    changes,
    save,
  }
}

export default usePluginEditor
export const PluginEditor = createContainer(usePluginEditor)
